import React, { useContext, useEffect, useState } from 'react';
import { DragIcon, SettingsIcon } from 'assests/GmsIcons';
import ToggleButton from 'Components/Common/components/Inputs/ToggleButton';
import { SET_MODES_PRIORITY_OBJECT, SET_MODES_PRIORITY_STRING } from '../ARSConstants';
import OverlayIcon from 'Components/GMS/Rules/Components/OverlayIcon';
import ARSSidePanel from './ARSSidepanel';
import ComputedSubstitutionComponent from 'Pages/GMS/ComputedSubstitutions/pages/ComputedSubstitutionView';
import RuleView from 'Pages/GMS/Rules/RuleView';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { updateRecommenderModel } from '../ARSServices/ARSServices';
import { ArsContext } from '../Contexts/ArsContext';
import { toast } from 'react-toastify';
import HotSeller from './HotSeller/HotSeller';
import Seasonal from './Seasonal/Seasonal';
import { TSetPriorityMode, TSubmodel } from '../Types';
import CuratedCategoryRelevancyTable from 'Pages/GMS/CategoryRelevancy/components/CuratedCategoryRelevancyTable';
import { CategoryRelevancyProvider } from 'Pages/GMS/CategoryRelevancy/contexts/CategoryRelevancyContext';

type TDefineModel = {
    setModePriority: TSetPriorityMode[]
    updateSetModePriority: (setModePriority: TSetPriorityMode[]) => void
    activeSubmodel: TSubmodel
    isDisabled?: boolean
    onExpertCuratedToggle?: (isEnabled: boolean) => void
}

type UpdateFieldType = {
    minNumProducts: number;
    title: string;
    active: boolean;
}

const ArsModelDetails = ({
    setModePriority,
    updateSetModePriority,
    activeSubmodel,
    isDisabled = false,
    onExpertCuratedToggle
}: TDefineModel) => {
    const [showSidePanel, setShowSidePanel] = useState<boolean>(false);
    const [modeKey, setModeKey] = useState<string>("");
    const [isFormValid, setIsFormValid] = useState<boolean>(true);
    const { model } = useContext(ArsContext);

    useEffect(() => {
        if (isDisabled && setModePriority) {
            const expertCuratedIndex = setModePriority.findIndex(mode => mode.mode === 'expert_curated_coseller');
            if (expertCuratedIndex !== -1 && setModePriority[expertCuratedIndex].active) {
                const updatedPriority = [...setModePriority];
                updatedPriority[expertCuratedIndex] = {
                    ...updatedPriority[expertCuratedIndex],
                    active: false
                };
                updateSetModePriority(updatedPriority);

                if (onExpertCuratedToggle) {
                    onExpertCuratedToggle(false);
                }
            }
        }
    }, [isDisabled]);

    const callUpdateSetModePriority = (index: number, name: keyof UpdateFieldType, value: string | boolean | number) => {
        const priority = [...setModePriority];
        if (name === "minNumProducts") {
            const numValue = Number(value);
            if (numValue >= 1) {
                priority[index] = {
                    ...priority[index],
                    minNumProducts: numValue
                };
            } else {
                priority[index] = {
                    ...priority[index],
                    minNumProducts: 1
                };
            }
        } else {
            priority[index] = {
                ...priority[index],
                [name]: value
            };
        }

        if (name === "active" && priority[index].mode === "expert_curated_coseller" && onExpertCuratedToggle) {
            onExpertCuratedToggle(value as boolean);
        }

        updateSetModePriority(priority);
    };

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) return;

        const items = Array.from(setModePriority);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        const updatedItems = items.map((item, index) => ({
            ...item,
            priority: index + 1
        }));

        updateSetModePriority(updatedItems);
    };

    const handleValidationChange = (isValid: boolean) => {
        setIsFormValid(isValid);
    };

    const onCloseSidePanel = () => {
        if ((modeKey === "seasonal" || modeKey === "hot_seller") && !isFormValid) {
            toast.error("Please set both start and end dates for Custom Date selection");
            return;
        }

        setShowSidePanel(false);
        setModeKey("");
    };

    return (
        <div className="w-h-[calc(100vh-171px)] tw-border tw-border-[#dadbdd] tw-mt-3 tw-ml-2 tw-rounded-sm tw-bg-white">
            <div className="tw-w-[100%] tw-h-11 tw-ps-2 tw-pt-2 tw-bg-white tw-rounded-tl tw-rounded-tr tw-border-b tw-border-[#dadbdd] tw-font-bold tw-text-[#6d788c]">
                {SET_MODES_PRIORITY_STRING}
            </div>
            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="setModePriority">
                    {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef}>
                            {setModePriority?.map((mode, index) => {
                                const isExpertCuratedMode = mode.mode === 'expert_curated_coseller';
                                const modeIsDisabled = isDisabled && isExpertCuratedMode;

                                return (
                                    <Draggable key={mode.mode} draggableId={mode.mode} index={index}>
                                        {(provided, snapshot) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                className={`
                                            tw-min-h-20 
                                            tw-w-[95%] md:tw-w-[97%] 
                                            tw-mx-2 md:tw-m-3 
                                            tw-p-2 md:tw-px-3 md:tw-py-2 
                                            tw-bg-[#f4f5f7] 
                                            tw-rounded 
                                            tw-flex 
                                            tw-flex-col md:tw-flex-row 
                                            tw-gap-3 md:tw-gap-[13px]
                                            ${snapshot.isDragging ? 'tw-opacity-70' : ''}
                                        `}
                                            >
                                                {/* Drag Handle and Number */}
                                                <div className="tw-flex tw-items-center tw-gap-2">
                                                    <DragIcon />
                                                    <div className="tw-text-[#6d788c] tw-text-xs tw-uppercase tw-leading-[14px]">
                                                        {index + 1}
                                                    </div>
                                                </div>

                                                <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-3 md:tw-gap-[13px]">
                                                    <div className="tw-flex tw-items-center tw-gap-2">
                                                        <div className="tw-text-[#172b4d] tw-text-xs tw-uppercase tw-leading-[14px]">
                                                            {SET_MODES_PRIORITY_OBJECT[mode?.mode]?.title}
                                                        </div>
                                                        <div className="tw-w-[15px] tw-h-[15px] tw-relative">
                                                            <OverlayIcon
                                                                message={
                                                                    mode?.description
                                                                        ? mode?.description
                                                                        : SET_MODES_PRIORITY_OBJECT[mode?.mode]?.description
                                                                }
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="tw-flex tw-flex-col md:tw-flex-row tw-gap-4 md:tw-gap-[13px]">
                                                        <div className="tw-flex tw-flex-col md:tw-flex-row md:tw-items-center tw-gap-2">
                                                            <div className="tw-text-[#6d788c] tw-text-sm tw-leading-tight">Title</div>
                                                            <input
                                                                className="tw-w-full md:tw-w-[350px] tw-h-9 tw-px-2.5 tw-py-2 tw-bg-gray-100 tw-rounded tw-border tw-border-[#ebecf0]"
                                                                value={mode?.title}
                                                                onChange={(e) => callUpdateSetModePriority(index, "title", e.target.value)}
                                                                disabled={modeIsDisabled}
                                                            />
                                                        </div>

                                                        <div className="tw-flex tw-flex-col md:tw-flex-row md:tw-items-center tw-gap-2">
                                                            <div className="tw-text-[#6d788c] tw-text-sm tw-leading-tight">
                                                                Minimum Products
                                                            </div>
                                                            <input
                                                                className="tw-w-full md:tw-w-[115px] tw-h-9 tw-px-2.5 tw-py-2 tw-bg-white tw-rounded tw-border tw-border-[#ebecf0]"
                                                                type="number"
                                                                min="1"
                                                                value={mode?.minNumProducts ?? 5}
                                                                onChange={(e) => callUpdateSetModePriority(index, "minNumProducts", e.target.value)}
                                                                disabled={modeIsDisabled}
                                                            />
                                                        </div>
                                                        <div className="tw-flex tw-items-center tw-gap-4 md:tw-gap-[13px] tw-mt-2 md:tw-mt-0">
                                                            <ToggleButton
                                                                name={`${index}`}
                                                                value={mode?.active}
                                                                onChange={(e) => callUpdateSetModePriority(index, "active", e.target.checked)}
                                                                disabled={modeIsDisabled}
                                                            />
                                                            <div className={`tw-flex tw-items-center ${mode?.mode === "compatible_coseller" || mode?.mode === "pure_compatibility" || mode?.mode === "hot_seller" || mode?.mode === "seasonal" || mode?.mode === "expert_curated_coseller" ? "" : "tw-opacity-20"} ${modeIsDisabled ? "tw-opacity-20" : ""}`} >
                                                                <button
                                                                    data-testid={`settings-${mode.mode}`}
                                                                    disabled={modeIsDisabled}
                                                                    onClick={
                                                                        mode?.mode === "compatible_coseller" || mode?.mode === "pure_compatibility" || mode?.mode === "hot_seller" || mode?.mode === "seasonal" || mode?.mode === "expert_curated_coseller"
                                                                            ? async () => {
                                                                                if (!model) return;
                                                                                const hasValidTitles = model?.submodel?.every(x =>
                                                                                    x.setModePriority.every(mode => mode.title?.trim().length > 0)
                                                                                );

                                                                                if (!hasValidTitles) {
                                                                                    toast.error("All mode titles are required. Please ensure each mode has a valid title.");
                                                                                    return;
                                                                                }
                                                                                const updateModelResponse = await updateRecommenderModel(model);
                                                                                if (updateModelResponse?.done) {
                                                                                    setModeKey(mode?.mode);
                                                                                    setShowSidePanel(true);
                                                                                    setIsFormValid(true);
                                                                                }
                                                                                else { toast.error(updateModelResponse?.message ?? "Failed to update the model") }
                                                                            }
                                                                            : () => { }
                                                                    }
                                                                >
                                                                    <SettingsIcon />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </Draggable>
                                );
                            })}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
            {showSidePanel ? (<ARSSidePanel show={showSidePanel} close={onCloseSidePanel} header={modeKey === "seasonal" ? "Seasonal" : modeKey === "hot_seller" ? "Hot Seller" : (modeKey === "compatible_coseller" || modeKey === "pure_compatibility") ? "Configure Rules" : modeKey === "expert_curated_coseller" ? "Curated Categories" : 'Configure Pivots'} overlayWidthInPercentage={["hot_seller", "seasonal"].includes(modeKey) ? 25 : 70}>
                <>
                    {modeKey === "expert_curated_coseller" ? (<CategoryRelevancyProvider initialTab="curated" showTabSelector={false}><CuratedCategoryRelevancyTable /></CategoryRelevancyProvider>) : null}
                    {modeKey === "compatible_coseller" ? <RuleView mode={modeKey} key={`rule-view-${activeSubmodel.submodel_id}`} baseCategories={activeSubmodel?.categories?.length ? activeSubmodel.categories : undefined} targetCategories={activeSubmodel?.target_categories?.length ? activeSubmodel.target_categories : undefined} /> : null}
                    {modeKey === "pure_compatibility" ? <RuleView mode={modeKey} key={`pure-compatibility-rule-view-${activeSubmodel.submodel_id}`} baseCategories={activeSubmodel?.categories?.length ? activeSubmodel.categories : undefined} targetCategories={activeSubmodel?.target_categories?.length ? activeSubmodel.target_categories : undefined} /> : null}
                    {modeKey === "computed_substitution" ? <ComputedSubstitutionComponent isSidePanel={true} key={`substitution-view-${activeSubmodel.submodel_id}`} baseCategories={activeSubmodel?.categories?.length ? activeSubmodel.categories : undefined} /> : null}
                    {modeKey === "hot_seller" ? (<HotSeller key={`hot-seller-view-${activeSubmodel.submodel_id}`} setModePriority={setModePriority} updateSetModePriority={updateSetModePriority} onValidationChange={handleValidationChange} />) : null}
                    {modeKey === "seasonal" ? (<Seasonal key={`seasonal-view-${activeSubmodel.submodel_id}`} setModePriority={setModePriority} updateSetModePriority={updateSetModePriority} onValidationChange={handleValidationChange} />) : null}
                </>
            </ARSSidePanel>) : null}
        </div>
    );
}

export default ArsModelDetails;